import type { PointerEvent as ReactPointerEvent } from 'react';

import { msAusX, xAusMs } from './leiste';
import styles from './Leisten.module.css';

/**
 * Die untere Leiste: zehn Vorschaubilder nebeneinander, darüber der Schiebestrich.
 *
 * **Die Bilder geben eine Ahnung, keine Genauigkeit** — sie sollen zeigen, wann ungefähr was zu
 * sehen ist, damit der Prüfer nicht blind zieht. Gezogen werden sie nativ
 * (`platform/videoFrames.ts`); fehlt eines, bleibt sein Platz leer und die Leiste bleibt
 * bedienbar.
 *
 * ⚠️ **Der Griff an die Leiste pausiert die Wiedergabe** — das entscheidet die Bühne, nicht
 * diese Leiste: ein weiterlaufendes Video zöge den Strich unter dem Finger weg.
 */

interface Props {
  /** Adressen der Vorschaubilder, in zeitlicher Folge. Leere Einträge bleiben Lücken. */
  bilder: string[];
  positionMs: number;
  durationMs: number;
  /** Meldet die Stelle, an die der Prüfer springen will. */
  onSpringen: (ms: number) => void;
  /** Beginn und Ende eines Zuges — die Bühne hält die Wiedergabe so lange an. */
  onZugStart: () => void;
  onZugEnde: () => void;
}

export function Zeitleiste({
  bilder,
  positionMs,
  durationMs,
  onSpringen,
  onZugStart,
  onZugEnde,
}: Props) {
  /** Bildschirm → Millisekunde. Die einzige Stelle, an der beide aufeinandertreffen. */
  const zuMs = (e: ReactPointerEvent<HTMLDivElement>): number => {
    const r = e.currentTarget.getBoundingClientRect();
    return msAusX(e.clientX - r.left, r.width, durationMs);
  };

  const down = (e: ReactPointerEvent<HTMLDivElement>) => {
    // Ohne das verliert die Leiste den Zeiger, sobald der Finger sie nach oben verlässt — und
    // der Strich bliebe mitten im Zug stehen.
    e.currentTarget.setPointerCapture(e.pointerId);
    onZugStart();
    onSpringen(zuMs(e));
  };

  const move = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (!e.currentTarget.hasPointerCapture(e.pointerId)) return;
    onSpringen(zuMs(e));
  };

  const up = (e: ReactPointerEvent<HTMLDivElement>) => {
    e.currentTarget.releasePointerCapture(e.pointerId);
    onZugEnde();
  };

  const anteil = durationMs > 0 ? xAusMs(positionMs, 100, durationMs) : 0;

  return (
    <div
      className={styles.zeitleiste}
      onPointerDown={down}
      onPointerMove={move}
      onPointerUp={up}
      onPointerCancel={up}
    >
      <div className={styles.streifen} aria-hidden="true">
        {bilder.map((quelle, i) =>
          quelle === '' ? (
            <div key={i} className={styles.luecke} />
          ) : (
            <img key={i} className={styles.vorschau} src={quelle} alt="" draggable={false} />
          ),
        )}
      </div>
      <div className={styles.strich} style={{ left: anteil + '%' }} />
    </div>
  );
}
